import { Extension } from '@tiptap/core';

// Text outline via -webkit-text-stroke, stored on the textStyle mark as
// width (px) + color and rendered as the shorthand so the style attribute
// never contains the substring "color" (which the pasted-content
// neutralizer CSS keys on).

export const DEFAULT_OUTLINE_COLOR = '#dddddd';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        textOutline: {
            setTextOutline: (width: number, color: string) => ReturnType;
            unsetTextOutline: () => ReturnType;
        };
    }
}

export const TextOutline = Extension.create({
    name: 'textOutline',

    addGlobalAttributes() {
        return [
            {
                types: ['textStyle'],
                attributes: {
                    outlineWidth: {
                        default: null,
                        parseHTML: (element) => {
                            const width = element.style.getPropertyValue('-webkit-text-stroke-width');
                            if (!width) return null;
                            const parsed = parseFloat(width);
                            return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
                        },
                        renderHTML: (attributes) => {
                            if (!attributes.outlineWidth) return {};
                            const color = attributes.outlineColor || DEFAULT_OUTLINE_COLOR;
                            return { style: `-webkit-text-stroke: ${attributes.outlineWidth}px ${color}` };
                        },
                    },
                    outlineColor: {
                        default: null,
                        parseHTML: (element) =>
                            element.style.getPropertyValue('-webkit-text-stroke-color') || null,
                        // Rendered as part of outlineWidth's shorthand
                        renderHTML: () => ({}),
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            setTextOutline:
                (width: number, color: string) =>
                ({ chain }) =>
                    chain().setMark('textStyle', { outlineWidth: width, outlineColor: color }).run(),
            unsetTextOutline:
                () =>
                ({ chain }) =>
                    chain()
                        .setMark('textStyle', { outlineWidth: null, outlineColor: null })
                        .removeEmptyTextStyle()
                        .run(),
        };
    },
});
